import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Check, CheckCircle2, Circle, Eye, EyeOff, Lock, MailCheck } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AuthLayout, AuthLeftPanel, AuthRightPanel, ValidatedInput } from '../../../../components/design-system/auth';
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, PASSWORD_RULES, passwordChecks } from '../../../../common/utils/password-policy';

/**
 * The frame of the pages reached from an emailed or shared link: accepting an
 * invitation, resetting a password, confirming an email. Same look as login,
 * with a way back to it.
 */
export function AccountPage({ children }: { children: ReactNode }) {
    const { t } = useTranslation();
    const left = (
        <AuthLeftPanel
            isFading={false}
            fadeClassName="account-panel-fade"
            switchText={t('ACCOUNT.COMMON.NAV_TEXT')}
            switchLinkText={t('ACCOUNT.COMMON.NAV_BUTTON')}
            switchLinkHref="/login"
        >
            {children}
        </AuthLeftPanel>
    );
    return <AuthLayout leftPanel={left} rightPanel={<AuthRightPanel />} />;
}

export function AccountHeading({ title, subtitle }: { title: ReactNode; subtitle?: ReactNode }) {
    return (
        <div className="animate-form-item animate-delay-1 mb-8">
            <h1 className="font-heading mb-1 text-center text-2xl font-medium tracking-tight">{title}</h1>
            {subtitle && <p className="text-center text-sm text-muted-foreground">{subtitle}</p>}
        </div>
    );
}

type Tone = 'success' | 'error' | 'info';
const ICONS: Record<Tone, ReactNode> = {
    success: <CheckCircle2 />,
    error: <AlertCircle />,
    info: <MailCheck />,
};

/** An outcome in place of the form: done, check your inbox, or why the link doesn't work. */
export function AccountNotice({ tone, title, children, testId }: { tone: Tone; title?: ReactNode; children: ReactNode; testId?: string }) {
    return (
        <Alert variant={tone === 'error' ? 'destructive' : 'default'} className="mb-6" data-testid={testId} role={tone === 'error' ? 'alert' : 'status'}>
            {ICONS[tone]}
            {title && <AlertTitle>{title}</AlertTitle>}
            <AlertDescription>{children}</AlertDescription>
        </Alert>
    );
}

/** Each rule for a new password, ticked off as it's met. */
export function PasswordRules({ password }: { password: string }) {
    const { t } = useTranslation();
    const checks = passwordChecks(password);
    return (
        <ul className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-2" data-testid="password-rules" aria-label={t('ACCOUNT.COMMON.PASSWORD_RULES_LABEL')}>
            {PASSWORD_RULES.map((rule) => (
                <li
                    key={rule}
                    data-rule={rule}
                    data-ok={checks[rule]}
                    className={`flex items-center gap-1.5 text-xs ${checks[rule] ? 'text-success' : 'text-muted-foreground'}`}
                >
                    {checks[rule] ? <Check className="size-3.5 shrink-0" aria-hidden /> : <Circle className="size-3.5 shrink-0" aria-hidden />}
                    <span>
                        {t(`ACCOUNT.COMMON.PASSWORD_RULES.${rule.toUpperCase()}`, { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH })}
                        <span className="sr-only">{checks[rule] ? ` ${t('ACCOUNT.COMMON.RULE_MET')}` : ` ${t('ACCOUNT.COMMON.RULE_NOT_MET')}`}</span>
                    </span>
                </li>
            ))}
        </ul>
    );
}

/** A password input with a show/hide toggle; for a new password, the rules listed under it. */
export function PasswordField({
    label, value, onChange, error, errorMessage, onKeyDown, className, autoComplete = 'new-password', showRules = false,
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    error?: boolean;
    errorMessage?: string;
    onKeyDown?: (e: any) => void;
    className?: string;
    autoComplete?: string;
    showRules?: boolean;
}) {
    const { t } = useTranslation();
    const [show, setShow] = useState(false);
    return (
        <div className={className}>
        <ValidatedInput
            label={label}
            placeholder="••••••••"
            value={value}
            onChange={onChange}
            onKeyDown={onKeyDown}
            type={show ? 'text' : 'password'}
            autoComplete={autoComplete}
            error={error}
            errorMessage={errorMessage}
            startIcon={<Lock />}
            endIcon={
                <button
                    type="button"
                    onClick={() => setShow(!show)}
                    aria-label={show ? t('ACCOUNT.COMMON.HIDE_PASSWORD') : t('ACCOUNT.COMMON.SHOW_PASSWORD')}
                    className="flex items-center transition-colors hover:text-foreground"
                >
                    {show ? <EyeOff /> : <Eye />}
                </button>
            }
        />
        {showRules && <PasswordRules password={value} />}
        </div>
    );
}

export const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
